/**
 * Weight milestone chart shown on the farm dashboard.
 *
 * Mirrors `GET /dashboard/weights` (swagger:
 * `HealthSystem.API.Controllers.Dashboards.Responses.WeightMilestoneDashboardResponse`). Hand-written
 * rather than taken from the generated client, for the same reason `piglet-registration.ts` is - see
 * `WeightMilestoneService`. Field names match the API exactly so the two stay easy to diff.
 */

/** The three bands, in order. Ordinal and colour-free: the palette lives in `colors.ts`. */
export const WEIGHT_BANDS = ['Low', 'Medium', 'High'] as const;

export type WeightBand = (typeof WEIGHT_BANDS)[number];

/**
 * The thresholds the API split the counts by, in grams. Below `mediumFromGrams` is low, above
 * `mediumToGrams` is high, and both thresholds themselves are medium. Sent rather than hardcoded here
 * because they are backend configuration and can be retuned without a frontend release.
 */
export interface WeightBands {
  mediumFromGrams: number;
  mediumToGrams: number;
}

export interface WeightMilestoneWeek {
  /** First day of the week. Carried alongside the number because week numbers repeat across a year. */
  weekStartDate: string;
  year: number;
  weekNumber: number;
  /**
   * Piglets **born** in this week that were weighed at birth - the sum of the three band counts. The
   * week is the birth week, not the day the scale reading was taken, so this chart and the registration
   * chart above it describe the same weeks. A farm weighing a whole batch at once stamps one date on
   * every animal in it, which is why the two rules are not interchangeable.
   */
  measuredCount: number;
  /** Mean weight in grams as recorded, or null when nothing was weighed - never zero for "nothing". */
  averageGrams: number | null;
  lowCount: number;
  mediumCount: number;
  highCount: number;
  /**
   * The same readings projected back to the day of birth: a piglet weighed 1350 g on day two was born at
   * 1150 g, and that lower figure is what the rest of the system stores as its birth weight. The same
   * piglets, so these three counts stack to `measuredCount` too - the two stacks of a week differ in how
   * the piglets split across the bands, never in height.
   */
  calculatedAverageGrams: number | null;
  calculatedLowCount: number;
  calculatedMediumCount: number;
  calculatedHighCount: number;
}

export interface WeightMilestoneSeries {
  /** A `WeightMilestoneEnumeration` id. Only `Birthed` is sent so far. */
  milestone: string;
  bands: WeightBands | null;
  /** Piglets weighed across the whole period. */
  measuredCount: number;
  /** Mean weight in grams as recorded across the period, or null when nothing was measured. */
  averageGrams: number | null;
  lowCount: number;
  mediumCount: number;
  highCount: number;
  /** The period's totals for the back-calculated birth weight - see `WeightMilestoneWeek`. */
  calculatedAverageGrams: number | null;
  calculatedLowCount: number;
  calculatedMediumCount: number;
  calculatedHighCount: number;
  /** One entry per week of the period, oldest first, empty weeks included. */
  weeks: WeightMilestoneWeek[];
}

export interface WeightMilestoneDashboard {
  weeksCount: number;
  /** First day of the earliest week in the period. */
  fromDate: string;
  series: WeightMilestoneSeries[];
}

export const BIRTH_WEIGHT_MILESTONE = 'Birthed';

/**
 * The birth weight series, or null while the response is still in flight. Looked up by id rather than
 * taken as `series[0]`, so adding the weaned and ten week series later cannot silently repoint this
 * chart at another milestone.
 */
export const getBirthWeightSeries = (dashboard: WeightMilestoneDashboard | null): WeightMilestoneSeries | null =>
  dashboard?.series.find((series) => series.milestone === BIRTH_WEIGHT_MILESTONE) ?? null;
